import { useState, useEffect, useCallback } from 'react';
import PageHeader from '../components/ui/page-header';
import { FeedbackButton } from '../components/ui/feedback-button';
import { HelpButton } from '../components/ui/help-button';
import DecisionRow from '../components/DecisionRow';
import { useAuth } from '../context/AuthContext';
import MainLayout from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { RightPanel } from '../components/ui/right-panel';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer
} from 'recharts';
import {
  Target, AlertCircle, CheckCircle2, Loader2, Plus, Edit, Trash2, FileSearch,
  Info, Scale, Shield, CalendarClock, MessageSquare, RotateCcw, Clock, ExternalLink,
  ArrowLeft, ArrowRight, Package, Eye, Send, CheckSquare, TrendingUp, Zap, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import RegulationsFeed from '../components/regulations/RegulationsFeed';
import { getUserErrorMessage } from '../utils/errorMessages';

const DEFAULT_DOMAINS = [
  { domain: 'Governance', huidig: 2.5, doel: 4.5 },
  { domain: 'Risk mgmt', huidig: 2.5, doel: 4.0 },
  { domain: 'Awareness', huidig: 2.5, doel: 3.5 },
  { domain: 'Detection', huidig: 2.5, doel: 3.5 },
  { domain: 'Response', huidig: 2.5, doel: 4.0 },
  { domain: 'Resilience', huidig: 2.5, doel: 4.0 },
];
const DEFAULT_PILLARS = [
  { name: 'Governance & beleid', progress: 2.5, color: 'bg-neutral-400' },
  { name: 'Risicomanagement', progress: 2.5, color: 'bg-neutral-400' },
  { name: 'Awareness & training', progress: 2.5, color: 'bg-neutral-400' },
  { name: 'Detectie & monitoring', progress: 2.5, color: 'bg-neutral-400' },
  { name: 'Response & crisishandling', progress: 2.5, color: 'bg-neutral-400' },
  { name: 'Continuïteit & herstel', progress: 2.5, color: 'bg-neutral-400' },
];
const STATUS_OPTIONS = [
  { value: 'gepland', label: 'Gepland', color: 'bg-neutral-100 text-neutral-700' },
  { value: 'in_uitvoering', label: 'In uitvoering', color: 'bg-amber-100 text-amber-700' },
  { value: 'voltooid', label: 'Voltooid', color: 'bg-emerald-100 text-emerald-700' },
];
const OWNER_OPTIONS = ['CISO', 'IT Ops', 'SecOps', 'Risk Manager', 'Compliance Officer', 'Board'];
const PLAN_STATUS_OPTIONS = [
  { value: 'actief', label: 'Actief', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'review', label: 'Review nodig', color: 'bg-amber-100 text-amber-700' },
  { value: 'concept', label: 'Concept', color: 'bg-neutral-100 text-neutral-600' },
];
const PRIORITY_OPTIONS = [
  { value: 'kritiek', label: 'Kritiek', color: 'bg-red-100 text-red-700' },
  { value: 'hoog', label: 'Hoog', color: 'bg-amber-100 text-amber-700' },
  { value: 'medium', label: 'Medium', color: 'bg-blue-100 text-blue-700' },
  { value: 'laag', label: 'Laag', color: 'bg-neutral-100 text-neutral-600' },
];
const RECOVERY_STATUS_OPTIONS = [
  { value: 'getest', label: 'Getest', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'gepland', label: 'Gepland', color: 'bg-neutral-100 text-neutral-600' },
  { value: 'concept', label: 'Concept', color: 'bg-amber-100 text-amber-700' },
];

const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return dateString; }
};

const StatusBadge = ({ value, options }) => {
  const opt = options.find(s => s.value === value);
  return <Badge className={opt?.color || 'bg-neutral-100'}>{opt?.label || value}</Badge>;
};

// =============================
// Follow-up Panel Component
// =============================
const FollowUpPanel = ({ followUps = [], onAdd, planId }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [note, setNote] = useState('');
  const [action, setAction] = useState('opvolging');
  const [scheduledDate, setScheduledDate] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!note.trim()) { toast.error('Vul een notitie in'); return; }
    setSaving(true);
    try {
      await onAdd(planId, { note, action, scheduled_date: scheduledDate || null });
      setShowAdd(false);
      setNote(''); setAction('opvolging'); setScheduledDate('');
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-3 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-neutral-500">Opvolgingen ({followUps.length})</span>
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShowAdd(!showAdd)} data-testid={`toggle-followup-${planId}`}>
          <Plus className="h-3 w-3 mr-1" strokeWidth={1.5} />{showAdd ? 'Annuleren' : 'Opvolging'}
        </Button>
      </div>
      {showAdd && (
        <div className="p-3 rounded-lg border border-neutral-200 space-y-3 bg-white">
          <Textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Beschrijf de opvolging..." rows={2} className="text-sm" data-testid={`followup-note-${planId}`} />
          <div className="flex gap-2">
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger className="h-8 text-xs flex-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="opvolging">Opvolging</SelectItem>
                <SelectItem value="hercontrole">Hercontrole</SelectItem>
                <SelectItem value="update">Bijwerken</SelectItem>
              </SelectContent>
            </Select>
            <Input type="date" value={scheduledDate} onChange={e => setScheduledDate(e.target.value)} className="h-8 text-xs flex-1" />
            <Button size="sm" className="h-8 bg-black text-white hover:bg-neutral-800 text-xs" onClick={handleSubmit} disabled={saving} data-testid={`submit-followup-${planId}`}>
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Opslaan'}
            </Button>
          </div>
        </div>
      )}
      {followUps.length > 0 && (
        <div className="space-y-2.5 max-h-40 overflow-y-auto">
          {followUps.slice().reverse().map((fu, i) => (
            <div key={fu.id || i} className="flex items-start gap-2 p-2 rounded bg-neutral-50 text-xs">
              <div className={`mt-0.5 h-4 w-4 rounded-full flex items-center justify-center shrink-0 ${
                fu.action === 'hercontrole' ? 'bg-blue-100 text-blue-600' : fu.action === 'update' ? 'bg-amber-100 text-amber-600' : 'bg-emerald-100 text-emerald-600'
              }`}>
                {fu.action === 'hercontrole' ? <RotateCcw className="h-2.5 w-2.5" /> : fu.action === 'update' ? <Edit className="h-2.5 w-2.5" /> : <MessageSquare className="h-2.5 w-2.5" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-neutral-700">{fu.note}</p>
                <p className="text-neutral-400 mt-0.5">
                  {fu.created_by} - {formatDate(fu.created_at)}
                  {fu.scheduled_date && <span className="ml-2">Gepland: {formatDate(fu.scheduled_date)}</span>}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// =============================
// Main StrategiePage Component
// =============================
import { PageShell, PageToolbar, SectionTabs, SECTIONS } from '../components/enterprise';

const StrategiePage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const urlTab = searchParams.get('tab');
  const validTabs = ['veiligheidsstrategie', 'beleidstukken', 'compliance', 'wet-regelgeving', 'certificering'];
  const [activeTab, setActiveTab] = useState(validTabs.includes(urlTab) ? urlTab : 'veiligheidsstrategie');
  const [maturityData, setMaturityData] = useState(null);
  const [actions, setActions] = useState([]);
  const [emergencyPlans, setEmergencyPlans] = useState([]);
  const [recoveryPlans, setRecoveryPlans] = useState([]);
  const [showActionModal, setShowActionModal] = useState(false);
  const [editingAction, setEditingAction] = useState(null);
  const [saving, setSaving] = useState(false);
  // Emergency plan modal
  const [showEPModal, setShowEPModal] = useState(false);
  const [editingEP, setEditingEP] = useState(null);
  const [epForm, setEpForm] = useState({ name: '', owner: 'CISO', status: 'actief', description: '', last_test_date: '', next_review_date: '' });
  // Recovery plan modal
  const [showRPModal, setShowRPModal] = useState(false);
  const [editingRP, setEditingRP] = useState(null);
  const [rpForm, setRpForm] = useState({ system: '', priority: 'medium', rto: '4h', rpo: '1h', backup_strategy: '', status: 'gepland' });
  // Wet & regelgeving sub-tab
  const [regSubTab, setRegSubTab] = useState('overheid');
  // Beleidsdocument popup
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  // Controle aanvragen popup
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestDoc, setRequestDoc] = useState(null);
  const [requestForm, setRequestForm] = useState({ request_type: 'controle', description: '', preferred_date: '' });
  const [requestSending, setRequestSending] = useState(false);
  // Compliance mapping
  const [complianceData, setComplianceData] = useState(null);
  const [complianceLoading, setComplianceLoading] = useState(false);
  const [complianceFw, setComplianceFw] = useState('');
  // Policy documents from backend
  const [policyDocs, setPolicyDocs] = useState([]);
  const [policyLoading, setPolicyLoading] = useState(false);
  const [showTransitionModal, setShowTransitionModal] = useState(false);
  const [transitionDoc, setTransitionDoc] = useState(null);
  const [transitionTarget, setTransitionTarget] = useState('');
  const [transitionComment, setTransitionComment] = useState('');
  const [transitionSaving, setTransitionSaving] = useState(false);
  const [showNewPolicyModal, setShowNewPolicyModal] = useState(false);
  const [newPolicyForm, setNewPolicyForm] = useState({ name: '', category: 'Governance', description: '', owner: '', reviewer: '' });
  const [newPolicySaving, setNewPolicySaving] = useState(false);


  const handleServiceRequest = async () => {
    setRequestSending(true);
    try {
      await api.post('/ggrc/service-requests', {
        request_type: requestForm.request_type,
        document_name: requestDoc?.name || '',
        document_status: requestDoc?.status || '',
        description: requestForm.description,
        preferred_date: requestForm.preferred_date || null,
      });
      toast.success('Aanvraag succesvol verstuurd naar leverancier');
      setShowRequestModal(false);
      setRequestDoc(null);
      setRequestForm({ request_type: 'controle', description: '', preferred_date: '' });
    } catch {
      toast.error('Aanvraag kon niet worden verstuurd');
    } finally {
      setRequestSending(false);
    }
  };

  const [formData, setFormData] = useState({ actie: '', doel: '', eigenaar: 'CISO', status: 'gepland', deadline: '' });
  const [actionWizardStep, setActionWizardStep] = useState(1);
  const [epWizardStep, setEpWizardStep] = useState(1);
  const [rpWizardStep, setRpWizardStep] = useState(1);

  const fetchData = useCallback(async () => {
    try {
      const [maturityRes, actionsRes, epRes, rpRes] = await Promise.all([
        api.get('/ggrc/strategy/maturity').catch(() => ({ data: null })),
        api.get('/ggrc/strategy/actions').catch(() => ({ data: [] })),
        api.get('/ggrc/emergency-plans').catch(() => ({ data: [] })),
        api.get('/ggrc/recovery-plans').catch(() => ({ data: [] })),
      ]);
      setMaturityData(maturityRes.data);
      setActions(actionsRes.data || []);
      setEmergencyPlans(epRes.data || []);
      setRecoveryPlans(rpRes.data || []);
    } catch (error) {
      console.error('Error fetching strategy data:', error);
    } finally { setLoading(false); }
  }, [api]);

  const fetchPolicyDocs = useCallback(async () => {
    setPolicyLoading(true);
    try {
      const res = await api.get('/ggrc/policy-documents');
      setPolicyDocs(res.data.documents || []);
    } catch { setPolicyDocs([]); }
    finally { setPolicyLoading(false); }
  }, [api]);

  const fetchComplianceMapping = useCallback(async (fwId) => {
    setComplianceLoading(true);
    try {
      const url = fwId ? `/ggrc/compliance-mapping?framework_id=${fwId}` : '/ggrc/compliance-mapping';
      const res = await api.get(url);
      setComplianceData(res.data);
      if (!complianceFw && res.data.framework_id) setComplianceFw(res.data.framework_id);
    } catch { setComplianceData(null); }
    finally { setComplianceLoading(false); }
  }, [api, complianceFw]);

  useEffect(() => { fetchData(); fetchPolicyDocs(); }, [fetchData, fetchPolicyDocs]);

  // Auto-load compliance when tab changes
  useEffect(() => {
    if (activeTab === 'compliance' && !complianceData && !complianceLoading) {
      fetchComplianceMapping();
    }
  }, [activeTab, complianceData, complianceLoading, fetchComplianceMapping]);

  // === Action CRUD ===
  const handleSaveAction = async () => {
    if (!formData.actie || !formData.doel) { toast.error('Vul minimaal actie en doel in'); return; }
    setSaving(true);
    try {
      if (editingAction) { await api.patch(`/ggrc/strategy/actions/${editingAction.id}`, formData); toast.success('Actie bijgewerkt'); }
      else { await api.post('/ggrc/strategy/actions', formData); toast.success('Actie toegevoegd'); }
      setShowActionModal(false); resetActionForm(); fetchData();
    } catch (e) { toast.error(getUserErrorMessage(e, 'Opslaan mislukt')); } finally { setSaving(false); }
  };
  const handleDeleteAction = async (id) => {
    if (!window.confirm('Weet u zeker dat u deze actie wilt verwijderen?')) return;
    try { await api.delete(`/ggrc/strategy/actions/${id}`); toast.success('Actie verwijderd'); fetchData(); } catch { toast.error('Verwijderen mislukt'); }
  };
  const openEditAction = (a) => {
    setEditingAction(a); setFormData({ actie: a.actie, doel: a.doel, eigenaar: a.eigenaar, status: a.status, deadline: a.deadline || '' }); setActionWizardStep(1); setShowActionModal(true);
  };
  const resetActionForm = () => { setEditingAction(null); setFormData({ actie: '', doel: '', eigenaar: 'CISO', status: 'gepland', deadline: '' }); };

  // === Emergency Plan CRUD ===
  const handleSaveEP = async () => {
    if (!epForm.name) { toast.error('Vul een naam in'); return; }
    setSaving(true);
    try {
      if (editingEP) { await api.patch(`/ggrc/emergency-plans/${editingEP.id}`, epForm); toast.success('Noodplan bijgewerkt'); }
      else { await api.post('/ggrc/emergency-plans', epForm); toast.success('Noodplan aangemaakt'); }
      setShowEPModal(false); resetEPForm(); fetchData();
    } catch (e) { toast.error(getUserErrorMessage(e, 'Opslaan mislukt')); } finally { setSaving(false); }
  };
  const handleDeleteEP = async (id) => {
    if (!window.confirm('Weet u zeker dat u dit plan wilt verwijderen?')) return;
    try { await api.delete(`/ggrc/emergency-plans/${id}`); toast.success('Noodplan verwijderd'); fetchData(); } catch { toast.error('Verwijderen mislukt'); }
  };
  const openEditEP = (p) => {
    setEditingEP(p); setEpForm({ name: p.name, owner: p.owner || 'CISO', status: p.status || 'actief', description: p.description || '', last_test_date: p.last_test_date || '', next_review_date: p.next_review_date || '' }); setShowEPModal(true);
  };
  const resetEPForm = () => { setEditingEP(null); setEpForm({ name: '', owner: 'CISO', status: 'actief', description: '', last_test_date: '', next_review_date: '' }); };
  const addEPFollowUp = async (planId, data) => {
    try { await api.post(`/ggrc/emergency-plans/${planId}/follow-up`, data); toast.success('Opvolging toegevoegd'); fetchData(); } catch { toast.error('Toevoegen mislukt'); }
  };

  // === Recovery Plan CRUD ===
  const handleSaveRP = async () => {
    if (!rpForm.system) { toast.error('Vul een systeem in'); return; }
    setSaving(true);
    try {
      if (editingRP) { await api.patch(`/ggrc/recovery-plans/${editingRP.id}`, rpForm); toast.success('Herstelplan bijgewerkt'); }
      else { await api.post('/ggrc/recovery-plans', rpForm); toast.success('Herstelplan aangemaakt'); }
      setShowRPModal(false); resetRPForm(); fetchData();
    } catch (e) { toast.error(getUserErrorMessage(e, 'Opslaan mislukt')); } finally { setSaving(false); }
  };
  const handleDeleteRP = async (id) => {
    if (!window.confirm('Weet u zeker dat u dit plan wilt verwijderen?')) return;
    try { await api.delete(`/ggrc/recovery-plans/${id}`); toast.success('Herstelplan verwijderd'); fetchData(); } catch { toast.error('Verwijderen mislukt'); }
  };
  const openEditRP = (p) => {
    setEditingRP(p); setRpForm({ system: p.system, priority: p.priority || 'medium', rto: p.rto || '4h', rpo: p.rpo || '1h', backup_strategy: p.backup_strategy || '', status: p.status || 'gepland' }); setShowRPModal(true);
  };
  const resetRPForm = () => { setEditingRP(null); setRpForm({ system: '', priority: 'medium', rto: '4h', rpo: '1h', backup_strategy: '', status: 'gepland' }); };
  const addRPFollowUp = async (planId, data) => {
    try { await api.post(`/ggrc/recovery-plans/${planId}/follow-up`, data); toast.success('Opvolging toegevoegd'); fetchData(); } catch { toast.error('Toevoegen mislukt'); }
  };

  // Policy document transition handler
  const handleTransition = async () => {
    if (!transitionDoc || !transitionTarget) return;
    setTransitionSaving(true);
    try {
      await api.post(`/ggrc/policy-documents/${transitionDoc.id}/transition`, {
        status: transitionTarget,
        comment: transitionComment,
      });
      toast.success(`Status gewijzigd naar ${transitionTarget}`);
      setShowTransitionModal(false);
      setTransitionDoc(null);
      setTransitionComment('');
      fetchPolicyDocs();
    } catch (e) {
      toast.error(getUserErrorMessage(e, 'Statuswijziging mislukt'));
    } finally { setTransitionSaving(false); }
  };

  // Create new policy document
  const handleCreatePolicy = async () => {
    if (!newPolicyForm.name.trim()) { toast.error('Vul een naam in'); return; }
    setNewPolicySaving(true);
    try {
      await api.post('/ggrc/policy-documents', newPolicyForm);
      toast.success('Document aangemaakt');
      setShowNewPolicyModal(false);
      setNewPolicyForm({ name: '', category: 'Governance', description: '', owner: '', reviewer: '' });
      fetchPolicyDocs();
    } catch (e) {
      toast.error(getUserErrorMessage(e, 'Aanmaken mislukt'));
    } finally { setNewPolicySaving(false); }
  };

  // Delete policy document
  const handleDeletePolicy = async (docId) => {
    if (!window.confirm('Weet u zeker dat u dit document wilt verwijderen?')) return;
    try {
      await api.delete(`/ggrc/policy-documents/${docId}`);
      toast.success('Document verwijderd');
      fetchPolicyDocs();
    } catch { toast.error('Verwijderen mislukt'); }
  };

  // === Computed ===
  const domains = maturityData?.domains?.length > 0 ? maturityData.domains : DEFAULT_DOMAINS;
  const pillars = maturityData?.pillars?.length > 0 ? maturityData.pillars : DEFAULT_PILLARS;
  const hasAssessments = maturityData?.has_assessments || { people: false, process: false, technology: false };
  const anyAssessmentDone = hasAssessments.people || hasAssessments.process || hasAssessments.technology;
  const kpis = maturityData?.kpis || {
    pijlers_op_koers: pillars.filter(p => p.progress >= 60).length,
    volwassenheid: (pillars.reduce((sum, p) => sum + p.progress, 0) / pillars.length).toFixed(0),
    open_acties: actions.filter(a => a.status !== 'voltooid').length,
    kritieke_gaps: pillars.filter(p => p.progress < 40).length
  };

  // Emergency plan stats
  const epActive = emergencyPlans.filter(p => p.status === 'actief').length;
  const epReview = emergencyPlans.filter(p => p.status === 'review').length;
  // Recovery plan stats
  const rpTested = recoveryPlans.filter(p => p.status === 'getest').length;
  const rpPlanned = recoveryPlans.filter(p => p.status === 'gepland').length;

  if (loading) {
    return <PageShell section="verbeteren" testId="strategie-page" loading />;
  }

  return (
    <PageShell section="verbeteren" testId="strategie-page">
      <PageToolbar
        title="Strategie"
        subtitle={kpis.open_acties > 0 ? `${kpis.open_acties} openstaande acties` : 'Bekijk en verbeter je beveiligingsniveau'}
        testId="strategie"
      />

      <div className="px-6 py-6">
          <div className="space-y-8">
            {/* ======== VERBETERPLAN ======== */}
            {(() => {
              const maturityLevel = Math.min(5, Math.max(1, Math.ceil(parseFloat(kpis.volwassenheid) / 20)));
              const nextLevel = Math.min(5, maturityLevel + 1);
              const levelLabels = { 1: 'Initieel', 2: 'Herhaalbaar', 3: 'Gedefinieerd', 4: 'Beheerst', 5: 'Geoptimaliseerd' };

              // Generate improvement steps based on current state
              const improvements = [];
              const lowPillars = pillars.filter(p => p.progress < 60);
              if (lowPillars.length > 0) {
                lowPillars.slice(0, 2).forEach(p => {
                  improvements.push({
                    label: `Verbeter ${p.name}`,
                    description: `Huidige score: ${p.progress}%. Breng dit naar minimaal 60% om het volgende niveau te bereiken.`,
                    priority: p.progress < 40 ? 'high' : 'medium',
                    href: null,
                    cta: 'Bekijk acties',
                  });
                });
              }
              const missingPolicies = ['Business Continuity Plan', 'Data Classificatie Beleid', 'Leveranciersbeheerbeleid'];
              if (missingPolicies.length > 0) {
                improvements.push({
                  label: 'Stel ontbrekende beleidsdocumenten op',
                  description: `${missingPolicies.length} beleidsdocumenten ontbreken nog. Deze zijn nodig voor een volledig beveiligingsraamwerk.`,
                  priority: 'medium',
                  href: null,
                  cta: 'Bekijk beleid',
                });
              }
              if (kpis.open_acties > 0) {
                improvements.push({
                  label: `Rond ${kpis.open_acties} openstaande acties af`,
                  description: 'Openstaande acties vertragen je voortgang. Pak de belangrijkste eerst aan.',
                  priority: kpis.kritieke_gaps > 0 ? 'high' : 'medium',
                  href: null,
                  cta: 'Bekijk acties',
                });
              }

              return (
                <section className="space-y-4" data-testid="verbeterplan">
                  <DecisionRow
                    label="Doelniveau"
                    value={`Van niveau ${maturityLevel} (${levelLabels[maturityLevel]}) naar ${nextLevel} (${levelLabels[nextLevel]})`}
                    type="recommended"
                    rationale={improvements.length > 0
                      ? `${improvements.length} verbeterpunten om het volgende niveau te bereiken. Bevestig je doelniveau om je verbeterplan te activeren.`
                      : 'Je bent goed op weg. Blijf monitoren en verbeteren.'}
                    confirmed={true}
                    onEdit={() => { const el = document.querySelector('[data-testid="actions-table"]'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}
                    editLabel="Acties bekijken"
                  >
                    <div className="flex items-center gap-1 mt-2">
                      {[1,2,3,4,5].map(l => (
                        <div key={l} className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                          l <= maturityLevel ? 'bg-black text-white' : l === nextLevel ? 'border-2 border-black text-black' : 'bg-neutral-100 text-neutral-400'
                        }`}>{l}</div>
                      ))}
                    </div>
                  </DecisionRow>
                  {improvements.length > 0 && (
                    <div className="space-y-3" data-testid="improvement-decisions">
                      {improvements.map((imp, i) => (
                        <DecisionRow
                          key={i}
                          label={i === 0 ? 'Prioriteit' : `Verbeterpunt ${i + 1}`}
                          value={imp.label}
                          type={imp.priority === 'high' ? 'required' : 'recommended'}
                          rationale={imp.description}
                          onConfirm={() => {
                            if (imp.cta === 'Bekijk beleid') setActiveTab('beleidstukken');
                            else { const el = document.querySelector('[data-testid="actions-table"]'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }
                          }}
                          confirmLabel={imp.cta}
                        />
                      ))}
                    </div>
                  )}
                </section>
              );
            })()}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="inline-flex justify-start bg-neutral-100 p-1 rounded-lg h-auto flex-wrap">
            {[
              { v: 'veiligheidsstrategie', l: 'Veiligheidsstrategie', icon: Shield },
              { v: 'beleidstukken', l: 'Beleidstukken', icon: Package },
              { v: 'compliance', l: 'Compliance', icon: FileSearch },
              { v: 'wet-regelgeving', l: 'Wet- & regelgeving', icon: Scale },
              { v: 'certificering', l: 'Certificering', icon: FileSearch },
            ].map(t => (
              <TabsTrigger key={t.v} value={t.v} className="rounded-md px-4 py-1.5 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm flex items-center gap-1.5"
                data-testid={`tab-${t.v}`}>
                <t.icon className="h-3.5 w-3.5" strokeWidth={1.5} />{t.l}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* ========== VEILIGHEIDSSTRATEGIE ========== */}
          <TabsContent value="veiligheidsstrategie" className="mt-6 space-y-6">
            {!anyAssessmentDone && (
              <Card className="border-amber-200 bg-amber-50 shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-start gap-6">
                    <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                      <Info className="h-5 w-5 text-amber-600" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-amber-800">Voltooi een assessment voor nauwkeurige data</h3>
                      <p className="text-xs text-amber-700 mt-1">De strategie dashboard toont momenteel standaard waarden. Voer minimaal een assessment uit om uw werkelijke security maturity te zien.</p>
                      <Button size="sm" className="bg-amber-600 text-white hover:bg-amber-700 mt-3" onClick={() => navigate('/assessment')} data-testid="start-assessment-button">
                        <FileSearch className="h-4 w-4 mr-1" strokeWidth={1.5} />Start Assessment
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {(() => {
                const maturityLevel = Math.min(5, Math.max(1, Math.ceil(parseFloat(kpis.volwassenheid) / 20)));
                return [
                  { val: `${maturityLevel}`, lbl: 'Volwassenheidsniveau', sub: `Niveau ${maturityLevel} van 5`, icon: Target, color: 'indigo' },
                  { val: kpis.pijlers_op_koers, lbl: 'Pijlers op koers (>60%)', icon: CheckCircle2, color: 'emerald' },
                  { val: kpis.open_acties, lbl: 'Open strategische acties', icon: Clock, color: 'amber' },
                  { val: kpis.kritieke_gaps, lbl: 'Kritieke gaps', icon: AlertCircle, color: 'red' },
                ].map((k, i) => (
                  <Card key={i} className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-3">
                        <div className={`h-10 w-10 rounded-lg bg-${k.color}-50 flex items-center justify-center`}>
                          <k.icon className={`h-5 w-5 text-${k.color}-600`} strokeWidth={1.5} />
                        </div>
                        <div>
                          <p className={`text-2xl font-bold text-${k.color === 'red' ? 'red-600' : 'black'}`}>{k.val}</p>
                          <p className="text-xs text-neutral-500">{k.lbl}</p>
                          {k.sub && <p className="text-[10px] text-neutral-400">{k.sub}</p>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ));
              })()}
            </div>

            {/* Level 1-5 Widget */}
            <Card className="border-neutral-200 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-black">Volwassenheidsniveau</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  {[1,2,3,4,5].map(level => {
                    const maturityLevel = Math.min(5, Math.max(1, Math.ceil(parseFloat(kpis.volwassenheid) / 20)));
                    const desiredLevel = 4;
                    const isCurrent = level <= maturityLevel;
                    const isDesired = level <= desiredLevel;
                    return (
                      <div key={level} className="flex-1 text-center">
                        <div className={`h-12 rounded-lg flex items-center justify-center font-bold text-lg transition-all ${
                          isCurrent ? 'bg-emerald-500 text-white' : isDesired ? 'bg-blue-100 text-blue-600 border-2 border-blue-300 border-dashed' : 'bg-neutral-100 text-neutral-400'
                        }`}>
                          {level}
                        </div>
                        <p className="text-[10px] text-neutral-500 mt-1">
                          {level === 1 && 'Initieel'}
                          {level === 2 && 'Herhaalbaar'}
                          {level === 3 && 'Gedefinieerd'}
                          {level === 4 && 'Beheerst'}
                          {level === 5 && 'Geoptimaliseerd'}
                        </p>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center gap-6 mt-3">
                  <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-emerald-500" /><span className="text-xs text-neutral-600">Huidig niveau</span></div>
                  <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-blue-100 border-2 border-blue-300 border-dashed" /><span className="text-xs text-neutral-600">Gewenst niveau</span></div>
                </div>
              </CardContent>
            </Card>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="border-neutral-200 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold text-black">Maturity per domein</CardTitle>
                  <div className="flex items-center gap-4 mt-2">
                    <div className="flex items-center gap-2"><div className="w-4 h-1 bg-emerald-500 rounded" /><span className="text-xs text-neutral-500">Huidig</span></div>
                    <div className="flex items-center gap-2"><div className="w-4 h-1 bg-blue-500 rounded" /><span className="text-xs text-neutral-500">Doel</span></div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="h-[300px] w-full min-w-0 overflow-hidden">
                    <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                      <RadarChart data={domains} margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
                        <PolarGrid stroke="#e5e5e5" />
                        <PolarAngleAxis dataKey="domain" tick={{ fill: '#737373', fontSize: 11 }} tickLine={false} />
                        <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: '#a3a3a3', fontSize: 10 }} tickCount={6} />
                        <Radar name="Huidig" dataKey="huidig" stroke="#10b981" fill="#10b981" fillOpacity={0.3} strokeWidth={2} />
                        <Radar name="Doel" dataKey="doel" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.1} strokeWidth={2} strokeDasharray="5 5" />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-neutral-200 shadow-sm">
                <CardHeader className="pb-2"><CardTitle className="text-base font-semibold text-black">Pijlers & voortgang</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  {pillars.map((p, i) => (
                    <div key={i} className="space-y-2.5">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-neutral-700">{p.name}</span>
                        <span className="text-sm font-semibold text-neutral-900">{p.progress}%</span>
                      </div>
                      <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
                        <div className={`h-full ${p.progress >= 60 ? 'bg-emerald-500' : p.progress >= 40 ? 'bg-amber-500' : 'bg-red-500'} rounded-full transition-all duration-500`} style={{ width: `${p.progress}%` }} />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
            <Card className="border-neutral-200 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-base font-semibold text-black">Strategische actielijst</CardTitle>
                <Button size="sm" className="bg-black text-white hover:bg-neutral-800" onClick={() => { resetActionForm(); setActionWizardStep(1); setShowActionModal(true); }} data-testid="add-action-button">
                  <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} />Actie toevoegen
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="spk-table" data-testid="actions-table">
                    <thead>
                      <tr>
                        {['Actie', 'Niveau', 'Doel', 'Eigenaar', 'Status', 'Deadline', ''].map((h, i) => (
                          <th key={i} className={i === 6 ? 'text-right' : ''}>{h || 'Acties'}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {actions.length > 0 ? actions.map(a => {
                        const level = a.level || 'operationeel';
                        const levelColors = { operationeel: 'bg-blue-50 text-blue-700', taktisch: 'bg-purple-50 text-purple-700', strategisch: 'bg-amber-50 text-amber-700' };
                        const levelLabels = { operationeel: 'Operationeel', taktisch: 'Taktisch', strategisch: 'Strategisch' };
                        return (
                        <tr key={a.id} data-testid={`action-row-${a.id}`}>
                          <td className="text-sm font-medium text-neutral-900">{a.actie}</td>
                          <td><Badge className={`text-xs ${levelColors[level]}`}>{levelLabels[level]}</Badge></td>
                          <td className="text-sm text-neutral-600">{a.doel}</td>
                          <td className="text-sm text-neutral-600">{a.eigenaar}</td>
                          <td><StatusBadge value={a.status} options={STATUS_OPTIONS} /></td>
                          <td className="text-sm text-neutral-600">{formatDate(a.deadline)}</td>
                          <td className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openEditAction(a)}><Edit className="h-4 w-4" strokeWidth={1.5} /></Button>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500 hover:text-red-700" onClick={() => handleDeleteAction(a.id)}><Trash2 className="h-4 w-4" strokeWidth={1.5} /></Button>
                            </div>
                          </td>
                        </tr>
                        );
                      }) : (
                        <tr><td colSpan={7} className="p-8 text-center">
                          <Target className="h-10 w-10 text-neutral-300 mx-auto mb-3" strokeWidth={1.5} />
                          <p className="text-sm font-medium text-neutral-600 mb-1">Nog geen strategische acties gedefinieerd</p>
                          <p className="text-xs text-neutral-400 mb-3">Voeg acties toe om je beveiligingsniveau te verhogen.</p>
                          <Button size="sm" className="bg-black text-white hover:bg-neutral-800" onClick={() => { resetActionForm(); setActionWizardStep(1); setShowActionModal(true); }}>
                            <Plus className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
                            Eerste actie toevoegen
                          </Button>
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ========== BELEIDSTUKKEN (Backend-driven + Review Workflow) ========== */}
          <TabsContent value="beleidstukken" className="mt-6 space-y-6" data-testid="tab-content-beleidstukken">
            <Card className="border-neutral-200 shadow-sm">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold text-black">Overzicht beleidsdocumenten</CardTitle>
                  <Button size="sm" className="bg-black text-white hover:bg-neutral-800" onClick={() => { setNewPolicyForm({ name: '', category: 'Governance', description: '', owner: '', reviewer: '' }); setShowNewPolicyModal(true); }} data-testid="add-policy-btn">
                    <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} />Nieuw document
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {policyLoading ? (
                  <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-neutral-400" /></div>
                ) : (
                <div className="overflow-x-auto rounded-xl border border-neutral-200">
                  <table className="spk-table" data-testid="policy-documents-table">
                    <thead>
                      <tr>
                        <th>Document</th>
                        <th>Categorie</th>
                        <th>Status</th>
                        <th>Eigenaar</th>
                        <th>Versie</th>
                        <th>Laatste update</th>
                        <th className="text-center">Acties</th>
                      </tr>
                    </thead>
                    <tbody>
                      {policyDocs.map((doc) => {
                        const statusMap = {
                          gepubliceerd: { label: 'Gepubliceerd', color: 'bg-emerald-100 text-emerald-700' },
                          goedgekeurd: { label: 'Goedgekeurd', color: 'bg-blue-100 text-blue-700' },
                          actief: { label: 'Actief', color: 'bg-emerald-100 text-emerald-700' },
                          review: { label: 'In review', color: 'bg-amber-100 text-amber-700' },
                          concept: { label: 'Concept', color: 'bg-neutral-100 text-neutral-600' },
                          ontbreekt: { label: 'Ontbreekt', color: 'bg-red-100 text-red-700' },
                        };
                        const s = statusMap[doc.status] || statusMap.ontbreekt;

                        // Determine next valid transitions
                        const transitions = {
                          ontbreekt: [{ target: 'concept', label: 'Start als concept', icon: Edit }],
                          concept: [{ target: 'review', label: 'Indienen voor review', icon: Send }],
                          review: [
                            { target: 'goedgekeurd', label: 'Goedkeuren', icon: CheckCircle2 },
                            { target: 'concept', label: 'Terugsturen', icon: ArrowLeft },
                          ],
                          goedgekeurd: [{ target: 'gepubliceerd', label: 'Publiceren', icon: ExternalLink }],
                          gepubliceerd: [{ target: 'review', label: 'Opnieuw reviewen', icon: Edit }],
                        };
                        const availableTransitions = transitions[doc.status] || [];

                        return (
                          <tr key={doc.id} data-testid={`policy-row-${doc.id}`} className="cursor-pointer hover:bg-neutral-50" onClick={() => setSelectedPolicy({ name: doc.name, cat: doc.category, status: doc.status, date: doc.updated_at })}>
                            <td>
                              <p className="text-sm font-medium text-black">{doc.name}</p>
                              {doc.description && <p className="text-xs text-neutral-500 mt-0.5 truncate max-w-[250px]">{doc.description}</p>}
                            </td>
                            <td><span className="text-xs text-neutral-600">{doc.category}</span></td>
                            <td><Badge className={`${s.color} text-[10px]`}>{s.label}</Badge></td>
                            <td><span className="text-xs text-neutral-600">{doc.owner || '-'}</span></td>
                            <td><span className="text-xs text-neutral-600">v{doc.version || 1}</span></td>
                            <td><span className="text-xs text-neutral-500">{doc.updated_at ? formatDate(doc.updated_at) : '-'}</span></td>
                            <td>
                              <div className="flex items-center justify-center gap-1 flex-wrap">
                                {availableTransitions.map((t, idx) => (
                                  <Button key={idx} size="sm" variant={idx === 0 ? 'default' : 'outline'}
                                    className={idx === 0 ? 'text-xs h-7 bg-black text-white hover:bg-neutral-800' : 'text-xs h-7 border-neutral-200'}
                                    onClick={() => { setTransitionDoc(doc); setTransitionTarget(t.target); setTransitionComment(''); setShowTransitionModal(true); }}
                                    data-testid={`policy-transition-${doc.id}-${t.target}`}>
                                    <t.icon className="h-3 w-3 mr-1" strokeWidth={1.5} />{t.label}
                                  </Button>
                                ))}
                                <Button size="sm" variant="outline" className="text-xs h-7 border-blue-200 text-blue-700 hover:bg-blue-50"
                                  onClick={() => { setRequestDoc(doc); setRequestForm({ request_type: 'controle', description: '', preferred_date: '' }); setShowRequestModal(true); }}
                                  data-testid={`request-review-${doc.id}`}>
                                  <Send className="h-3 w-3 mr-1" strokeWidth={1.5} />Controle
                                </Button>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-neutral-400 hover:text-red-500"
                                  onClick={() => handleDeletePolicy(doc.id)} data-testid={`delete-policy-${doc.id}`}>
                                  <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {policyDocs.length === 0 && (
                        <tr><td colSpan={7} className="p-8 text-center">
                          <Package className="h-10 w-10 text-neutral-300 mx-auto mb-3" strokeWidth={1.5} />
                          <p className="text-sm font-medium text-neutral-600 mb-1">Nog geen beleidsdocumenten</p>
                          <p className="text-xs text-neutral-400">Documenten worden automatisch aangemaakt bij het eerste bezoek.</p>
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                )}

                {/* Review History Section */}
                {policyDocs.some(d => d.review_history?.length > 0) && (
                  <div className="border-t border-neutral-200 p-4">
                    <p className="text-xs font-bold text-neutral-400 uppercase tracking-wider mb-3">Recente statuswijzigingen</p>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {policyDocs
                        .flatMap(d => (d.review_history || []).map(h => ({ ...h, doc_name: d.name })))
                        .sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''))
                        .slice(0, 10)
                        .map((h, i) => (
                          <div key={h.id || i} className="flex items-center gap-3 text-xs p-2 rounded bg-neutral-50">
                            <div className={`h-2 w-2 rounded-full shrink-0 ${
                              h.to_status === 'gepubliceerd' ? 'bg-emerald-500' :
                              h.to_status === 'goedgekeurd' ? 'bg-blue-500' :
                              h.to_status === 'review' ? 'bg-amber-500' : 'bg-neutral-400'
                            }`} />
                            <span className="font-medium text-black">{h.doc_name}</span>
                            <span className="text-neutral-400">{h.from_status} → {h.to_status}</span>
                            {h.comment && <span className="text-neutral-500 truncate max-w-[200px]">"{h.comment}"</span>}
                            <span className="ml-auto text-neutral-400">{h.user_name} - {formatDate(h.timestamp)}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Noodplannen */}
            <Card className="border-neutral-200 shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold text-black">Noodplannen</CardTitle>
                  <Button size="sm" className="bg-black text-white hover:bg-neutral-800" onClick={() => { resetEPForm(); setEpWizardStep(1); setShowEPModal(true); }} data-testid="add-emergency-plan-button">
                    <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} />Nieuw plan
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {emergencyPlans.length > 0 ? (
                  <div className="space-y-3">
                    {emergencyPlans.map(plan => (
                      <div key={plan.id} className="p-4 rounded-lg bg-neutral-50" data-testid={`ep-row-${plan.id}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`h-2 w-2 rounded-full ${plan.status === 'actief' ? 'bg-emerald-500' : plan.status === 'review' ? 'bg-amber-500' : 'bg-neutral-400'}`} />
                            <div>
                              <p className="text-sm font-medium text-black">{plan.name}</p>
                              <p className="text-xs text-neutral-500">Eigenaar: {plan.owner || '-'}{plan.description ? ` - ${plan.description}` : ''}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {plan.last_test_date && <div className="text-right hidden sm:block">
                              <p className="text-xs text-neutral-500">Laatste test</p>
                              <p className="text-xs font-medium text-black">{formatDate(plan.last_test_date)}</p>
                            </div>}
                            {plan.next_review_date && <div className="text-right hidden sm:block">
                              <p className="text-xs text-neutral-500">Volgende review</p>
                              <p className="text-xs font-medium text-black">{formatDate(plan.next_review_date)}</p>
                            </div>}
                            <StatusBadge value={plan.status} options={PLAN_STATUS_OPTIONS} />
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openEditEP(plan)}><Edit className="h-4 w-4" strokeWidth={1.5} /></Button>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500" onClick={() => handleDeleteEP(plan.id)}><Trash2 className="h-4 w-4" strokeWidth={1.5} /></Button>
                          </div>
                        </div>
                        <FollowUpPanel followUps={plan.follow_ups || []} onAdd={addEPFollowUp} planId={plan.id} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-8 text-center">
                    <Shield className="h-8 w-8 text-neutral-300 mx-auto mb-2" strokeWidth={1.5} />
                    <p className="text-sm font-medium text-neutral-600 mb-1">Nog geen noodplannen aangemaakt</p>
                    <p className="text-xs text-neutral-400">Maak een plan voor als er een beveiligingsincident plaatsvindt.</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Herstelplannen (Business Continuity) */}
            <Card className="border-neutral-200 shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold text-black">Herstelplannen</CardTitle>
                  <Button size="sm" className="bg-black text-white hover:bg-neutral-800" onClick={() => { resetRPForm(); setRpWizardStep(1); setShowRPModal(true); }} data-testid="add-recovery-plan-button">
                    <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} />Nieuw plan
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {recoveryPlans.length > 0 ? (
                  <div className="space-y-3">
                    {recoveryPlans.map(plan => (
                      <div key={plan.id} className="p-4 rounded-lg bg-neutral-50" data-testid={`rp-row-${plan.id}`}>
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-black">{plan.system}</p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-neutral-500 flex-wrap">
                              <span>RTO: <span className="font-semibold text-black">{plan.rto}</span></span>
                              <span>RPO: <span className="font-semibold text-black">{plan.rpo || '-'}</span></span>
                              {plan.backup_strategy && <span>Backup: {plan.backup_strategy}</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <StatusBadge value={plan.priority} options={PRIORITY_OPTIONS} />
                            <StatusBadge value={plan.status} options={RECOVERY_STATUS_OPTIONS} />
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openEditRP(plan)}><Edit className="h-4 w-4" strokeWidth={1.5} /></Button>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500" onClick={() => handleDeleteRP(plan.id)}><Trash2 className="h-4 w-4" strokeWidth={1.5} /></Button>
                          </div>
                        </div>
                        <FollowUpPanel followUps={plan.follow_ups || []} onAdd={addRPFollowUp} planId={plan.id} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-8 text-center">
                    <RotateCcw className="h-8 w-8 text-neutral-300 mx-auto mb-2" strokeWidth={1.5} />
                    <p className="text-sm font-medium text-neutral-600 mb-1">Nog geen herstelplannen aangemaakt</p>
                    <p className="text-xs text-neutral-400">Definieer hersteldoelen (RTO/RPO) voor je belangrijkste systemen.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ========== WET- & REGELGEVING (3 databronnen) ========== */}
          <TabsContent value="wet-regelgeving" className="mt-6 space-y-6">
            <div className="flex gap-2 flex-wrap">
              {[
                { v: 'overheid', l: 'Overheidsartikelen', icon: Scale },
                { v: 'nis2', l: 'NIS2 Richtlijn', icon: Shield },
                { v: 'avg', l: 'AVG / GDPR', icon: FileSearch },
              ].map(s => (
                <Button key={s.v} variant={regSubTab === s.v ? 'default' : 'outline'} size="sm"
                  className={regSubTab === s.v ? 'bg-black text-white hover:bg-neutral-800' : 'border-neutral-200'}
                  onClick={() => setRegSubTab(s.v)} data-testid={`reg-subtab-${s.v}`}>
                  <s.icon className="h-4 w-4 mr-1.5" strokeWidth={1.5} />{s.l}
                </Button>
              ))}
            </div>

            {regSubTab === 'overheid' && (
              <Card className="border-neutral-200 shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center">
                      <Scale className="h-5 w-5 text-white" strokeWidth={1.5} />
                    </div>
                    <div>
                      <CardTitle className="text-base font-semibold text-black">Overheidsartikelen</CardTitle>
                      <p className="text-xs text-neutral-500 mt-0.5">Actuele wet- en regelgeving van Ondernemersplein.nl</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent><RegulationsFeed /></CardContent>
              </Card>
            )}

            {regSubTab === 'nis2' && (
              <Card className="border-neutral-200 shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base font-semibold text-black">NIS2 Richtlijn</CardTitle>
                      <p className="text-xs text-neutral-500 mt-1">Network and Information Security Directive 2</p>
                    </div>
                    <Badge className="bg-amber-100 text-amber-700">In voorbereiding</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[
                      { name: 'Governance', color: 'emerald', pct: 75, desc: 'Beleid en verantwoordelijkheden vastgesteld' },
                      { name: 'Risicobeheer', color: 'amber', pct: 50, desc: 'Risicoanalyses en maatregelen' },
                      { name: 'Incidentmelding', color: 'red', pct: 25, desc: '24-uurs meldplicht procedure' },
                    ].map((item, i) => (
                      <div key={i} className="p-4 rounded-lg bg-neutral-50">
                        <div className="flex items-center gap-2 mb-2">
                          <div className={`h-2 w-2 rounded-full bg-${item.color}-500`} />
                          <span className="text-sm font-medium text-black">{item.name}</span>
                        </div>
                        <p className="text-xs text-neutral-500">{item.desc}</p>
                        <div className="mt-2 h-2 bg-neutral-200 rounded-full overflow-hidden">
                          <div className={`h-full bg-${item.color}-500 rounded-full`} style={{ width: `${item.pct}%` }} />
                        </div>
                        <p className="text-xs text-neutral-400 mt-1">{item.pct}% compliant</p>
                      </div>
                    ))}
                  </div>
                  <div className="p-4 rounded-lg border border-blue-100 bg-blue-50">
                    <div className="flex items-start gap-3">
                      <Info className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" strokeWidth={1.5} />
                      <div>
                        <p className="text-sm font-medium text-blue-800">Deadline: 17 oktober 2024</p>
                        <p className="text-xs text-blue-600 mt-1">NIS2 is inmiddels van kracht. Zorg dat uw organisatie voldoet aan alle vereisten.</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {regSubTab === 'avg' && (
              <Card className="border-neutral-200 shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base font-semibold text-black">AVG / GDPR</CardTitle>
                      <p className="text-xs text-neutral-500 mt-1">Algemene Verordening Gegevensbescherming</p>
                    </div>
                    <Badge className="bg-emerald-100 text-emerald-700">Grotendeels compliant</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    {[
                      { name: 'Verwerkingsregister', status: 'compliant', progress: 100 },
                      { name: 'Privacy beleid', status: 'compliant', progress: 100 },
                      { name: 'Verwerkersovereenkomsten', status: 'compliant', progress: 90 },
                      { name: 'Data Subject Rights', status: 'aandacht', progress: 70 },
                      { name: 'Privacy Impact Assessments', status: 'aandacht', progress: 60 },
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center gap-6">
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm text-black">{item.name}</span>
                            <span className="text-xs text-neutral-500">{item.progress}%</span>
                          </div>
                          <div className="h-1.5 bg-neutral-200 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${item.status === 'compliant' ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{ width: `${item.progress}%` }} />
                          </div>
                        </div>
                        {item.status === 'compliant'
                          ? <CheckCircle2 className="h-4 w-4 text-emerald-500" strokeWidth={1.5} />
                          : <AlertCircle className="h-4 w-4 text-amber-500" strokeWidth={1.5} />}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ========== WET- & REGELGEVING (3 databronnen) ========== */}
          <TabsContent value="certificering" className="mt-6 space-y-6">
            <Card className="border-2 border-dashed border-blue-300 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Target className="h-5 w-5 text-blue-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <CardTitle className="text-base font-semibold text-blue-900">Aanbevolen Certificeringen</CardTitle>
                    <p className="text-xs text-blue-600 mt-0.5">Op basis van uw branche en risicoprofiel. Activeer gerelateerde modules om te starten.</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[
                    { name: 'ISO 27001', badge: 'Aanbevolen', badgeColor: 'bg-blue-100 text-blue-700', desc: 'Internationaal erkende standaard voor informatiebeveiliging.', module: 'Compliance', moduleActive: true },
                    { name: 'NEN 7510', badge: 'Overweeg', badgeColor: 'bg-amber-100 text-amber-700', desc: 'Nederlandse norm voor informatiebeveiliging in de zorg.', module: 'Risicobeheer', moduleActive: true },
                    { name: 'SOC 2', badge: 'Aanbevolen', badgeColor: 'bg-blue-100 text-blue-700', desc: 'Audit rapport voor service organisaties en cloud dienstverleners.', module: 'Rapportages', moduleActive: false },
                  ].map((cert, i) => (
                    <div key={i} className="p-4 rounded-lg bg-white border border-blue-200">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold text-black">{cert.name}</h4>
                        <Badge className={cert.badgeColor}>{cert.badge}</Badge>
                      </div>
                      <p className="text-xs text-neutral-600 mb-3">{cert.desc}</p>
                      <div className="flex items-center gap-2 text-xs">
                        {cert.moduleActive
                          ? <><CheckCircle2 className="h-3 w-3 text-emerald-500" strokeWidth={1.5} /><span className="text-emerald-600">Module '{cert.module}' actief</span></>
                          : <><AlertCircle className="h-3 w-3 text-amber-500" strokeWidth={1.5} /><span className="text-amber-600">Activeer module '{cert.module}'</span>
                            <Button size="sm" variant="link" className="h-auto p-0 text-xs text-blue-600" onClick={() => navigate('/subscription')} data-testid={`activate-module-${cert.module}`}>
                              <ExternalLink className="h-3 w-3 mr-0.5" />Activeren
                            </Button></>}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="border-neutral-200 shadow-sm">
              <CardHeader className="pb-3"><CardTitle className="text-base font-semibold text-black">Actieve Certificeringen</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[
                    { name: 'ISO 27001:2022', status: 'Gecertificeerd', issuer: 'TUV Nederland', number: 'NL-2024-27001-12345', validUntil: '15 maart 2027', surveillance: 'Juni 2026', active: true },
                    { name: 'Cyber Essentials', status: 'Gecertificeerd', level: 'Plus', number: 'CE-NL-2025-98765', validUntil: '1 september 2026', active: true },
                  ].map((cert, i) => (
                    <div key={i} className={`p-4 rounded-lg ${cert.active ? 'border-2 border-emerald-200 bg-emerald-50' : 'border border-neutral-200'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold text-black">{cert.name}</h4>
                        <Badge className="bg-emerald-100 text-emerald-700">{cert.status}</Badge>
                      </div>
                      <div className="space-y-3 text-xs text-neutral-600">
                        {cert.issuer && <p><span className="font-medium">Certificeringsinstantie:</span> {cert.issuer}</p>}
                        {cert.level && <p><span className="font-medium">Level:</span> {cert.level}</p>}
                        <p><span className="font-medium">Certificaatnummer:</span> {cert.number}</p>
                        <p><span className="font-medium">Geldig tot:</span> {cert.validUntil}</p>
                        {cert.surveillance && <p><span className="font-medium">Surveillance audit:</span> {cert.surveillance}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="border-neutral-200 shadow-sm">
              <CardHeader className="pb-3"><CardTitle className="text-base font-semibold text-black">Certificering Roadmap</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { cert: 'NIST CSF 2.0', status: 'in_progress', progress: 45, target: 'Q2 2026', phase: 'Framework mapping afgerond', module: 'Risk Management' },
                    { cert: 'SOC 2 Type II', status: 'in_progress', progress: 65, target: 'Q2 2026', phase: 'Gap assessment afgerond', module: 'Rapportages' },
                    { cert: 'ISO 22301 (BCM)', status: 'planned', progress: 15, target: 'Q4 2026', phase: 'Planning fase', module: 'Compliance' },
                    { cert: 'ISAE 3402', status: 'planned', progress: 0, target: '2027', phase: 'Nog niet gestart', module: 'Compliance' },
                  ].map((item, idx) => (
                    <div key={idx} className="p-4 rounded-lg bg-neutral-50">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="text-sm font-semibold text-black">{item.cert}</h4>
                          <p className="text-xs text-neutral-500">Target: {item.target} - Module: {item.module}</p>
                        </div>
                        <Badge className={item.status === 'in_progress' ? 'bg-blue-100 text-blue-700' : 'bg-neutral-100 text-neutral-600'}>
                          {item.status === 'in_progress' ? 'In uitvoering' : 'Gepland'}
                        </Badge>
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-neutral-500">{item.phase}</span>
                          <span className="font-medium text-black">{item.progress}%</span>
                        </div>
                        <div className="h-2 bg-neutral-200 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${item.status === 'in_progress' ? 'bg-blue-500' : 'bg-neutral-400'}`} style={{ width: `${item.progress}%` }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="border-neutral-200 shadow-sm">
              <CardHeader className="pb-3"><CardTitle className="text-base font-semibold text-black">Audit Kalender 2026</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { q: 'Q1', text: 'ISO 27001 interne audit' },
                    { q: 'Q2', text: 'SOC 2 Type II audit' },
                    { q: 'Q3', text: 'ISO 27001 surveillance' },
                    { q: 'Q4', text: 'ISO 22301 pre-audit' },
                  ].map((item, idx) => (
                    <div key={idx} className="p-3 rounded-lg border border-neutral-200 text-center">
                      <p className="text-xs font-semibold text-neutral-500 mb-2">{item.q}</p>
                      <p className="text-xs text-black">{item.text}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ========== COMPLIANCE MAPPING ========== */}
          <TabsContent value="compliance" className="mt-6 space-y-6" data-testid="tab-content-compliance">
            {complianceLoading ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-neutral-400" /></div>
            ) : complianceData ? (() => {
              const gap = complianceData.gap_analysis || {};
              const gapSummary = gap.summary || {};
              const gapItems = gap.items || [];
              const topPriorities = gapItems.slice(0, 5);
              const quickWins = gapItems.filter(g => g.effort === 'klein').slice(0, 3);

              const urgencyConfig = {
                kritiek: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', badge: 'bg-red-100 text-red-700', icon: AlertTriangle },
                hoog: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', badge: 'bg-orange-100 text-orange-700', icon: AlertCircle },
                gemiddeld: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700', icon: TrendingUp },
                laag: { bg: 'bg-neutral-50', border: 'border-neutral-200', text: 'text-neutral-600', badge: 'bg-neutral-100 text-neutral-600', icon: Info },
              };
              const effortLabels = { klein: 'Kleine inspanning', gemiddeld: 'Gemiddelde inspanning', groot: 'Grote inspanning' };

              return (
              <>
                {/* Framework selector + summary */}
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <h2 className="text-lg font-semibold text-black">{complianceData.framework_name} {complianceData.framework_version}</h2>
                    <p className="text-xs text-neutral-500 mt-1">Compliance dekking en gap-analyse op basis van risico-impact</p>
                  </div>
                  <Select value={complianceFw} onValueChange={v => { setComplianceFw(v); fetchComplianceMapping(v); }}>
                    <SelectTrigger className="w-48 h-9 text-sm" data-testid="compliance-fw-select"><SelectValue placeholder="Framework" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="iso27001_2022">ISO 27001:2022</SelectItem>
                      <SelectItem value="nis2">NIS2</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Summary cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { val: complianceData.summary.coverage_pct + '%', lbl: 'Dekking', color: complianceData.summary.coverage_pct >= 75 ? 'text-emerald-600' : complianceData.summary.coverage_pct >= 40 ? 'text-amber-600' : 'text-red-600' },
                    { val: complianceData.summary.covered, lbl: 'Volledig gedekt', color: 'text-emerald-600' },
                    { val: complianceData.summary.partial, lbl: 'Gedeeltelijk', color: 'text-amber-600' },
                    { val: complianceData.summary.missing, lbl: 'Ontbreekt', color: 'text-red-600' },
                  ].map((k, i) => (
                    <Card key={i} className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
                      <CardContent className="p-4">
                        <p className={`text-2xl font-bold ${k.color}`}>{k.val}</p>
                        <p className="text-xs text-neutral-500">{k.lbl}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Coverage bar */}
                <div className="h-3 bg-neutral-100 rounded-full overflow-hidden flex" data-testid="compliance-coverage-bar">
                  <div className="bg-emerald-500 h-full transition-all" style={{ width: `${(complianceData.summary.covered / complianceData.summary.total_controls) * 100}%` }} />
                  <div className="bg-amber-400 h-full transition-all" style={{ width: `${(complianceData.summary.partial / complianceData.summary.total_controls) * 100}%` }} />
                  <div className="bg-red-300 h-full transition-all" style={{ width: `${(complianceData.summary.missing / complianceData.summary.total_controls) * 100}%` }} />
                </div>
                <div className="flex items-center gap-6 text-xs text-neutral-500">
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-emerald-500" />Gedekt</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-400" />Gedeeltelijk</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-red-300" />Ontbreekt</span>
                </div>

                {/* ====== GAP ANALYSE SECTIE ====== */}
                {gapItems.length > 0 && (
                  <>
                  {/* Strategie-koppeling — verbindt compliance met actie */}
                  <div className="space-y-1" data-testid="compliance-strategy-link">
                    <DecisionRow
                      label="Compliance-strategie"
                      value={gapSummary.kritiek > 0
                        ? `${gapSummary.kritiek} kritieke gap${gapSummary.kritiek !== 1 ? 's' : ''} moeten eerst opgelost worden`
                        : gapSummary.hoog > 0
                          ? `${gapSummary.hoog} hoge-prioriteit gap${gapSummary.hoog !== 1 ? 's' : ''} verdienen aandacht`
                          : 'Geen kritieke compliance-gaps. Focus op verbetering'}
                      type={gapSummary.kritiek > 0 ? 'required' : gapSummary.hoog > 0 ? 'recommended' : 'auto'}
                      rationale={`${gapSummary.total_gaps} openstaande controls. ${quickWins.length} quick wins beschikbaar met kleine inspanning.`}
                      confirmed={gapSummary.kritiek === 0 && gapSummary.hoog === 0}
                      onConfirm={gapSummary.kritiek > 0 || gapSummary.hoog > 0 ? () => navigate('/measures') : undefined}
                      confirmLabel="Maatregelen toevoegen"
                    />
                    {quickWins.length > 0 && (
                      <DecisionRow
                        label="Quick wins"
                        value={`${quickWins.length} controls met kleine inspanning beschikbaar`}
                        type="recommended"
                        rationale="Begin met deze quick wins voor directe compliance-winst"
                        onConfirm={() => {
                          const el = document.querySelector('[data-testid="gap-quick-wins"]');
                          if (el) el.scrollIntoView({ behavior: 'smooth' });
                        }}
                        confirmLabel="Bekijk quick wins"
                      />
                    )}
                  </div>

                  <Card className="border-neutral-200 shadow-sm" data-testid="gap-analysis-card">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-amber-500 to-red-500 flex items-center justify-center">
                            <TrendingUp className="h-4 w-4 text-white" strokeWidth={2} />
                          </div>
                          <div>
                            <CardTitle className="text-base font-semibold text-black">Gap-analyse</CardTitle>
                            <p className="text-xs text-neutral-500">{gapSummary.total_gaps} openstaande controls, geprioriteerd op risico-impact</p>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-5">
                      {/* Urgency distribution */}
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="gap-urgency-cards">
                        {[
                          { key: 'kritiek', label: 'Kritiek', icon: AlertTriangle, color: 'red' },
                          { key: 'hoog', label: 'Hoog', icon: AlertCircle, color: 'orange' },
                          { key: 'gemiddeld', label: 'Gemiddeld', icon: TrendingUp, color: 'amber' },
                          { key: 'laag', label: 'Laag', icon: Info, color: 'neutral' },
                        ].map(u => (
                          <div key={u.key} className={`rounded-xl border p-3 ${
                            u.color === 'red' ? 'bg-red-50 border-red-200' :
                            u.color === 'orange' ? 'bg-orange-50 border-orange-200' :
                            u.color === 'amber' ? 'bg-amber-50 border-amber-200' :
                            'bg-neutral-50 border-neutral-200'
                          }`}>
                            <div className="flex items-center gap-2 mb-1">
                              <u.icon className={`h-3.5 w-3.5 ${
                                u.color === 'red' ? 'text-red-500' :
                                u.color === 'orange' ? 'text-orange-500' :
                                u.color === 'amber' ? 'text-amber-500' :
                                'text-neutral-400'
                              }`} strokeWidth={1.5} />
                              <span className="text-xs font-medium text-neutral-600">{u.label}</span>
                            </div>
                            <p className={`text-xl font-bold ${
                              u.color === 'red' ? 'text-red-700' :
                              u.color === 'orange' ? 'text-orange-700' :
                              u.color === 'amber' ? 'text-amber-700' :
                              'text-neutral-700'
                            }`}>{gapSummary[u.key] || 0}</p>
                          </div>
                        ))}
                      </div>

                      {/* Quick Wins */}
                      {quickWins.length > 0 && (
                        <div className="space-y-2" data-testid="gap-quick-wins">
                          <div className="flex items-center gap-2 mb-1">
                            <Zap className="h-4 w-4 text-amber-500" strokeWidth={1.5} />
                            <p className="text-sm font-semibold text-black">Quick wins</p>
                            <span className="text-xs text-neutral-400">Kleine inspanning, directe compliance-winst</span>
                          </div>
                          {quickWins.map(g => {
                            const cfg = urgencyConfig[g.urgency] || urgencyConfig.laag;
                            return (
                              <div key={g.control_id} className={`flex items-center gap-3 p-3 rounded-xl border ${cfg.bg} ${cfg.border}`}>
                                <CheckCircle2 className={`h-5 w-5 shrink-0 ${cfg.text}`} strokeWidth={1.5} />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-mono font-bold text-neutral-800">{g.control_code}</span>
                                    <span className="text-sm font-medium text-black">{g.control_name}</span>
                                  </div>
                                  <p className="text-xs text-neutral-600 mt-0.5">{g.recommendation}</p>
                                </div>
                                <Badge className={`${cfg.badge} text-[10px] shrink-0`}>{g.urgency}</Badge>
                                <Button size="sm" className="bg-black text-white hover:bg-neutral-800 text-xs h-7 shrink-0" onClick={() => navigate('/measures')} data-testid={`qw-action-${g.control_id}`}>
                                  Oplossen
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Top Priorities */}
                      <div className="space-y-2" data-testid="gap-top-priorities">
                        <p className="text-sm font-semibold text-black mb-1">Prioriteiten op basis van risico-impact</p>
                        <div className="overflow-x-auto rounded-xl border border-neutral-200">
                          <table className="spk-table" data-testid="gap-priority-table">
                            <thead>
                              <tr>
                                <th className="w-8">#</th>
                                <th>Control</th>
                                <th>Categorie</th>
                                <th className="text-center">Score</th>
                                <th className="text-center">Urgentie</th>
                                <th className="text-center">Inspanning</th>
                                <th className="text-center">Risico's</th>
                                <th>Aanbeveling</th>
                              </tr>
                            </thead>
                            <tbody>
                              {topPriorities.map((g, idx) => {
                                const cfg = urgencyConfig[g.urgency] || urgencyConfig.laag;
                                const UrgIcon = cfg.icon;
                                return (
                                  <tr key={g.control_id} data-testid={`gap-row-${g.control_id}`}>
                                    <td className="text-xs font-bold text-neutral-400">{idx + 1}</td>
                                    <td>
                                      <span className="text-xs font-mono font-bold text-neutral-800">{g.control_code}</span>
                                      <span className="text-xs text-neutral-700 ml-1.5">{g.control_name}</span>
                                    </td>
                                    <td><span className="text-xs text-neutral-500">{g.category}</span></td>
                                    <td className="text-center">
                                      <div className="inline-flex items-center gap-1">
                                        <div className="w-12 h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                                          <div className={`h-full rounded-full transition-all ${
                                            g.priority_score >= 20 ? 'bg-red-500' :
                                            g.priority_score >= 12 ? 'bg-orange-500' :
                                            g.priority_score >= 7 ? 'bg-amber-500' : 'bg-neutral-400'
                                          }`} style={{ width: `${Math.min(g.priority_score / 25 * 100, 100)}%` }} />
                                        </div>
                                        <span className="text-[10px] font-mono text-neutral-500">{g.priority_score}</span>
                                      </div>
                                    </td>
                                    <td className="text-center">
                                      <Badge className={`${cfg.badge} text-[10px]`}>
                                        <UrgIcon className="h-2.5 w-2.5 mr-0.5" strokeWidth={1.5} />{g.urgency}
                                      </Badge>
                                    </td>
                                    <td className="text-center">
                                      <Badge className={`text-[10px] ${
                                        g.effort === 'klein' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                                        g.effort === 'gemiddeld' ? 'bg-amber-50 text-amber-700 border-amber-100' :
                                        'bg-red-50 text-red-700 border-red-100'
                                      }`}>{effortLabels[g.effort]}</Badge>
                                    </td>
                                    <td className="text-center">
                                      <span className="text-xs font-medium text-neutral-700">{g.linked_risks_count}</span>
                                    </td>
                                    <td><span className="text-xs text-neutral-600">{g.recommendation}</span></td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {gapItems.length > 5 && (
                          <div className="pt-2 text-center">
                            <Button variant="outline" size="sm" className="text-xs border-neutral-200" onClick={() => {
                              const el = document.querySelector('[data-testid="compliance-controls-list"]');
                              if (el) el.scrollIntoView({ behavior: 'smooth' });
                            }} data-testid="show-all-gaps-btn">
                              Alle {gapItems.length} gaps bekijken
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                  </>
                )}

                {/* Controls as DecisionRows */}
                <div className="space-y-3" data-testid="compliance-controls-list">
                  <p className="text-sm font-semibold text-black">Alle controls ({complianceData.controls.length})</p>
                  {complianceData.controls.map(ctrl => {
                    // Find gap info for this control
                    const gapInfo = gapItems.find(g => g.control_id === ctrl.id);

                    return (
                    <DecisionRow
                      key={ctrl.id}
                      label={ctrl.code}
                      value={ctrl.name}
                      type={ctrl.decision_type}
                      confirmed={ctrl.status === 'gedekt'}
                      rationale={
                        ctrl.status === 'gedekt'
                          ? `Gedekt door ${ctrl.measures.length} maatregel(en) en ${ctrl.policies.length} beleidsdocument(en).`
                          : ctrl.status === 'gedeeltelijk'
                            ? `Gedeeltelijk gedekt. ${ctrl.measures.length > 0 ? ctrl.measures.length + ' maatregel(en)' : 'Geen maatregelen'}, ${ctrl.policies.length > 0 ? ctrl.policies.length + ' beleidsdocument(en)' : 'geen beleidsdocumenten'} gekoppeld.`
                            : `Geen maatregelen of beleid gekoppeld aan deze control.`
                      }
                      onConfirm={ctrl.status !== 'gedekt' ? () => navigate('/measures') : undefined}
                      confirmLabel={ctrl.status === 'ontbreekt' ? 'Maatregel toevoegen' : 'Bekijk maatregelen'}
                    >
                      {/* Gap priority badge */}
                      {gapInfo && (
                        <div className="flex items-center gap-2 mt-1.5">
                          <Badge className={`text-[10px] ${(urgencyConfig[gapInfo.urgency] || urgencyConfig.laag).badge}`}>
                            Prioriteit: {gapInfo.priority_score} ({gapInfo.urgency})
                          </Badge>
                          <Badge className={`text-[10px] ${
                            gapInfo.effort === 'klein' ? 'bg-emerald-50 text-emerald-700' :
                            gapInfo.effort === 'gemiddeld' ? 'bg-amber-50 text-amber-700' :
                            'bg-red-50 text-red-700'
                          }`}>{effortLabels[gapInfo.effort]}</Badge>
                        </div>
                      )}
                      {/* Linked items */}
                      {(ctrl.measures.length > 0 || ctrl.policies.length > 0 || ctrl.risks.length > 0) && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {ctrl.measures.map(m => (
                            <Badge key={m.id} className="bg-blue-50 text-blue-700 text-[10px] border-blue-100 cursor-pointer hover:bg-blue-100" onClick={() => navigate('/measures')} data-testid={`compliance-measure-${m.id}`}>
                              <CheckSquare className="h-2.5 w-2.5 mr-1" strokeWidth={1.5} />{m.name}
                            </Badge>
                          ))}
                          {ctrl.policies.map(p => (
                            <Badge key={p.id} className="bg-indigo-50 text-indigo-700 text-[10px] border-indigo-100">
                              <Package className="h-2.5 w-2.5 mr-1" strokeWidth={1.5} />{p.name}
                            </Badge>
                          ))}
                          {ctrl.risks.map(r => (
                            <Badge key={r.id} className="bg-purple-50 text-purple-700 text-[10px] border-purple-100 cursor-pointer hover:bg-purple-100" onClick={() => navigate('/risks')}>
                              <Shield className="h-2.5 w-2.5 mr-1" strokeWidth={1.5} />{r.title}
                            </Badge>
                          ))}
                        </div>
                      )}
                      <p className="text-[10px] text-neutral-400 mt-1">{ctrl.category} · {ctrl.description}</p>
                    </DecisionRow>
                  );
                  })}
                </div>
              </>
              );
            })() : (
              <div className="py-16 text-center">
                <FileSearch className="h-10 w-10 text-neutral-300 mx-auto mb-3" strokeWidth={1.5} />
                <p className="text-sm text-neutral-600 mb-3">Laad de compliance mapping om te zien hoe je maatregelen en beleid zich verhouden tot framework controls.</p>
                <Button className="bg-black text-white hover:bg-neutral-800" onClick={() => fetchComplianceMapping()} data-testid="load-compliance-btn">
                  <FileSearch className="h-4 w-4 mr-2" strokeWidth={1.5} />Compliance mapping laden
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Uitbreidingen Tab */}
        </Tabs>
          </div>
        </div>

      <RightPanel
        open={!!selectedPolicy}
        onClose={() => setSelectedPolicy(null)}
        title={selectedPolicy?.name || 'Beleidsdocument'}
        subtitle={selectedPolicy?.cat}
        testId="policy-detail-panel"
      >
        {selectedPolicy && (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">Status</p>
              <Badge className={selectedPolicy.status === 'actief' ? 'bg-emerald-50 text-emerald-700' : selectedPolicy.status === 'concept' ? 'bg-amber-50 text-amber-700' : selectedPolicy.status === 'review' ? 'bg-blue-50 text-blue-700' : 'bg-red-50 text-red-700'}>
                {selectedPolicy.status === 'actief' ? 'Actief' : selectedPolicy.status === 'concept' ? 'Concept' : selectedPolicy.status === 'review' ? 'Review nodig' : 'Ontbreekt'}
              </Badge>
            </div>
            {selectedPolicy.date && (
              <div className="flex items-center justify-between py-2 border-b border-neutral-100">
                <span className="text-xs text-neutral-500">Datum</span>
                <span className="text-xs font-semibold text-black">{formatDate(selectedPolicy.date)}</span>
              </div>
            )}
            <div className="p-4 rounded-xl bg-neutral-50 border border-neutral-100">
              <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider mb-2">Aanbevolen acties</p>
              {selectedPolicy.status === 'ontbreekt' ? (
                <div className="space-y-3">
                  <p className="text-xs text-neutral-600">Dit beleidsdocument ontbreekt nog. Aanbevolen stappen:</p>
                  <ul className="space-y-3 text-xs text-neutral-600">
                    <li className="flex items-start gap-1.5"><span className="text-neutral-400">1.</span> Stel een verantwoordelijke aan (bijv. CISO)</li>
                    <li className="flex items-start gap-1.5"><span className="text-neutral-400">2.</span> Gebruik het relevante framework als basis</li>
                    <li className="flex items-start gap-1.5"><span className="text-neutral-400">3.</span> Laat het document reviewen door het management</li>
                    <li className="flex items-start gap-1.5"><span className="text-neutral-400">4.</span> Plan een jaarlijkse review cyclus in</li>
                  </ul>
                </div>
              ) : selectedPolicy.status === 'review' ? (
                <p className="text-xs text-blue-600">Dit document heeft een review nodig. Plan een sessie in met de verantwoordelijke om het document te actualiseren.</p>
              ) : selectedPolicy.status === 'concept' ? (
                <p className="text-xs text-amber-600">Dit document is in concept. Laat het reviewen en goedkeuren door het management om het officieel te maken.</p>
              ) : (
                <p className="text-xs text-emerald-600">Dit document is actueel en actief. Zorg dat de volgende review vóór de verloopdatum plaatsvindt.</p>
              )}
            </div>
          </div>
        )}
      </RightPanel>

      <Dialog open={showActionModal} onOpenChange={setShowActionModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingAction ? 'Actie bewerken' : 'Nieuwe actie'}</DialogTitle>
            <DialogDescription>
              {actionWizardStep === 1 && 'Stap 1 van 3: Doel & beschrijving'}
              {actionWizardStep === 2 && 'Stap 2 van 3: Eigenaar & deadline'}
              {actionWizardStep === 3 && 'Stap 3 van 3: Status & bevestiging'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-1 px-1" data-testid="action-wizard-steps">
            {[1,2,3].map(s => (<div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${s <= actionWizardStep ? 'bg-black' : 'bg-neutral-200'}`} />))}
          </div>
          <div className="space-y-4 py-2">
            {actionWizardStep === 1 && (
              <>
                <div className="space-y-3"><Label htmlFor="actie">Actie *</Label><Input id="actie" value={formData.actie} onChange={e => setFormData({ ...formData, actie: e.target.value })} placeholder="bijv. Formaliseren securitybeleid" data-testid="action-actie-input" /></div>
                <div className="space-y-3"><Label htmlFor="doel">Doel *</Label><Input id="doel" value={formData.doel} onChange={e => setFormData({ ...formData, doel: e.target.value })} placeholder="bijv. Governance borgen" data-testid="action-doel-input" /></div>
              </>
            )}
            {actionWizardStep === 2 && (
              <>
                <div className="space-y-3"><Label>Eigenaar</Label><Select value={formData.eigenaar} onValueChange={v => setFormData({ ...formData, eigenaar: v })}><SelectTrigger data-testid="action-eigenaar-select"><SelectValue /></SelectTrigger><SelectContent>{OWNER_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-3"><Label htmlFor="deadline">Deadline</Label><Input id="deadline" type="date" value={formData.deadline} onChange={e => setFormData({ ...formData, deadline: e.target.value })} data-testid="action-deadline-input" /></div>
              </>
            )}
            {actionWizardStep === 3 && (
              <>
                <div className="space-y-3"><Label>Status</Label><Select value={formData.status} onValueChange={v => setFormData({ ...formData, status: v })}><SelectTrigger data-testid="action-status-select"><SelectValue /></SelectTrigger><SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select></div>
                <div className="p-4 bg-neutral-50 rounded-lg border border-neutral-200 space-y-2" data-testid="action-wizard-summary">
                  <p className="text-xs font-bold uppercase tracking-wider text-neutral-400">Samenvatting</p>
                  <div className="flex justify-between text-sm"><span className="text-neutral-500">Actie</span><span className="font-medium text-black">{formData.actie || '-'}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-neutral-500">Doel</span><span className="text-black">{formData.doel || '-'}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-neutral-500">Eigenaar</span><span className="text-black">{formData.eigenaar}</span></div>
                  {formData.deadline && <div className="flex justify-between text-sm"><span className="text-neutral-500">Deadline</span><span className="text-black">{formatDate(formData.deadline)}</span></div>}
                </div>
              </>
            )}
          </div>
          <DialogFooter className="gap-2">
            {actionWizardStep > 1 && <Button variant="outline" onClick={() => setActionWizardStep(actionWizardStep - 1)} data-testid="action-wizard-back"><ArrowLeft className="h-4 w-4 mr-1" strokeWidth={1.5} />Terug</Button>}
            <div className="flex-1" />
            {actionWizardStep < 3 ? (
              <Button className="bg-black text-white hover:bg-neutral-800" onClick={() => { if (actionWizardStep === 1 && (!formData.actie || !formData.doel)) { toast.error('Vul actie en doel in'); return; } setActionWizardStep(actionWizardStep + 1); }} data-testid="action-wizard-next">Volgende<ArrowRight className="h-4 w-4 ml-1" strokeWidth={1.5} /></Button>
            ) : (
              <Button className="bg-black text-white hover:bg-neutral-800" onClick={handleSaveAction} disabled={saving} data-testid="save-action-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editingAction ? 'Opslaan' : 'Toevoegen')}</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === Emergency Plan Wizard === */}
      <Dialog open={showEPModal} onOpenChange={setShowEPModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingEP ? 'Noodplan bewerken' : 'Nieuw noodplan'}</DialogTitle>
            <DialogDescription>{epWizardStep === 1 ? 'Stap 1 van 2: Naam & beschrijving' : 'Stap 2 van 2: Eigenaar, status & planning'}</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-1 px-1" data-testid="ep-wizard-steps">
            {[1,2].map(s => (<div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${s <= epWizardStep ? 'bg-black' : 'bg-neutral-200'}`} />))}
          </div>
          <div className="space-y-4 py-2">
            {epWizardStep === 1 && (
              <>
                <div className="space-y-3"><Label>Naam *</Label><Input value={epForm.name} onChange={e => setEpForm({ ...epForm, name: e.target.value })} placeholder="bijv. Ransomware Response" data-testid="ep-name-input" /></div>
                <div className="space-y-3"><Label>Beschrijving</Label><Textarea value={epForm.description} onChange={e => setEpForm({ ...epForm, description: e.target.value })} placeholder="Beschrijf het noodplan..." rows={3} data-testid="ep-description-input" /></div>
              </>
            )}
            {epWizardStep === 2 && (
              <>
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-3"><Label>Eigenaar</Label><Select value={epForm.owner} onValueChange={v => setEpForm({ ...epForm, owner: v })}><SelectTrigger data-testid="ep-owner-select"><SelectValue /></SelectTrigger><SelectContent>{OWNER_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select></div>
                  <div className="space-y-3"><Label>Status</Label><Select value={epForm.status} onValueChange={v => setEpForm({ ...epForm, status: v })}><SelectTrigger data-testid="ep-status-select"><SelectValue /></SelectTrigger><SelectContent>{PLAN_STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select></div>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-3"><Label>Laatste test</Label><Input type="date" value={epForm.last_test_date} onChange={e => setEpForm({ ...epForm, last_test_date: e.target.value })} data-testid="ep-last-test-input" /></div>
                  <div className="space-y-3"><Label>Volgende review</Label><Input type="date" value={epForm.next_review_date} onChange={e => setEpForm({ ...epForm, next_review_date: e.target.value })} data-testid="ep-next-review-input" /></div>
                </div>
              </>
            )}
          </div>
          <DialogFooter className="gap-2">
            {epWizardStep > 1 && <Button variant="outline" onClick={() => setEpWizardStep(1)} data-testid="ep-wizard-back"><ArrowLeft className="h-4 w-4 mr-1" strokeWidth={1.5} />Terug</Button>}
            <div className="flex-1" />
            {epWizardStep < 2 ? (
              <Button className="bg-black text-white hover:bg-neutral-800" onClick={() => { if (!epForm.name) { toast.error('Vul een naam in'); return; } setEpWizardStep(2); }} data-testid="ep-wizard-next">Volgende<ArrowRight className="h-4 w-4 ml-1" strokeWidth={1.5} /></Button>
            ) : (
              <Button className="bg-black text-white hover:bg-neutral-800" onClick={handleSaveEP} disabled={saving} data-testid="save-ep-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editingEP ? 'Opslaan' : 'Toevoegen')}</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === Recovery Plan Wizard === */}
      <Dialog open={showRPModal} onOpenChange={setShowRPModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingRP ? 'Herstelplan bewerken' : 'Nieuw herstelplan'}</DialogTitle>
            <DialogDescription>{rpWizardStep === 1 ? 'Stap 1 van 2: Systeem & prioriteit' : 'Stap 2 van 2: Timing & backup'}</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-1 px-1" data-testid="rp-wizard-steps">
            {[1,2].map(s => (<div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${s <= rpWizardStep ? 'bg-black' : 'bg-neutral-200'}`} />))}
          </div>
          <div className="space-y-4 py-2">
            {rpWizardStep === 1 && (
              <>
                <div className="space-y-3"><Label>Systeem / Service *</Label><Input value={rpForm.system} onChange={e => setRpForm({ ...rpForm, system: e.target.value })} placeholder="bijv. Primaire applicaties" data-testid="rp-system-input" /></div>
                <div className="space-y-3"><Label>Prioriteit</Label><Select value={rpForm.priority} onValueChange={v => setRpForm({ ...rpForm, priority: v })}><SelectTrigger data-testid="rp-priority-select"><SelectValue /></SelectTrigger><SelectContent>{PRIORITY_OPTIONS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent></Select></div>
              </>
            )}
            {rpWizardStep === 2 && (
              <>
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-3"><Label>RTO</Label><Input value={rpForm.rto} onChange={e => setRpForm({ ...rpForm, rto: e.target.value })} placeholder="bijv. 4h" data-testid="rp-rto-input" /></div>
                  <div className="space-y-3"><Label>RPO</Label><Input value={rpForm.rpo} onChange={e => setRpForm({ ...rpForm, rpo: e.target.value })} placeholder="bijv. 1h" data-testid="rp-rpo-input" /></div>
                </div>
                <div className="space-y-3"><Label>Backup strategie</Label><Input value={rpForm.backup_strategy} onChange={e => setRpForm({ ...rpForm, backup_strategy: e.target.value })} placeholder="bijv. Hot standby" data-testid="rp-backup-input" /></div>
                <div className="space-y-3"><Label>Status</Label><Select value={rpForm.status} onValueChange={v => setRpForm({ ...rpForm, status: v })}><SelectTrigger data-testid="rp-status-select"><SelectValue /></SelectTrigger><SelectContent>{RECOVERY_STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select></div>
              </>
            )}
          </div>
          <DialogFooter className="gap-2">
            {rpWizardStep > 1 && <Button variant="outline" onClick={() => setRpWizardStep(1)} data-testid="rp-wizard-back"><ArrowLeft className="h-4 w-4 mr-1" strokeWidth={1.5} />Terug</Button>}
            <div className="flex-1" />
            {rpWizardStep < 2 ? (
              <Button className="bg-black text-white hover:bg-neutral-800" onClick={() => { if (!rpForm.system) { toast.error('Vul een systeem in'); return; } setRpWizardStep(2); }} data-testid="rp-wizard-next">Volgende<ArrowRight className="h-4 w-4 ml-1" strokeWidth={1.5} /></Button>
            ) : (
              <Button className="bg-black text-white hover:bg-neutral-800" onClick={handleSaveRP} disabled={saving} data-testid="save-rp-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editingRP ? 'Opslaan' : 'Toevoegen')}</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* =============== CONTROLE AANVRAGEN DIALOG =============== */}
      <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
        <DialogContent className="sm:max-w-[500px]" data-testid="request-review-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Controle aanvragen</DialogTitle>
            <DialogDescription className="text-xs">
              Vraag een leverancier om dit beleidsdocument te beoordelen en eventueel bij te sturen
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {requestDoc && (
              <div className="p-3 bg-neutral-50 rounded-xl border border-neutral-100">
                <p className="text-[10px] font-bold uppercase text-neutral-400 mb-1">Document</p>
                <p className="text-sm font-medium text-black">{requestDoc.name}</p>
                <p className="text-[10px] text-neutral-500">Status: {requestDoc.status}</p>
              </div>
            )}
            <div className="space-y-3">
              <Label className="text-xs font-medium">Type aanvraag</Label>
              <Select value={requestForm.request_type} onValueChange={v => setRequestForm(f => ({ ...f, request_type: v }))}>
                <SelectTrigger data-testid="request-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="controle">Controle & beoordeling</SelectItem>
                  <SelectItem value="advies">Afspraak voor advies / toelichting</SelectItem>
                  <SelectItem value="template">Template aanvragen voor documentatie</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-3">
              <Label className="text-xs font-medium">Toelichting</Label>
              <textarea
                className="w-full min-h-[80px] rounded-lg border border-neutral-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
                placeholder="Beschrijf wat u precies nodig heeft..."
                value={requestForm.description}
                onChange={e => setRequestForm(f => ({ ...f, description: e.target.value }))}
                data-testid="request-description"
              />
            </div>
            {requestForm.request_type === 'advies' && (
              <div className="space-y-3">
                <Label className="text-xs font-medium">Gewenste datum voor afspraak</Label>
                <Input 
                  type="date" 
                  value={requestForm.preferred_date} 
                  onChange={e => setRequestForm(f => ({ ...f, preferred_date: e.target.value }))}
                  data-testid="request-date"
                />
              </div>
            )}
            <div className="p-3 bg-blue-50 rounded-xl border border-blue-100">
              <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wider mb-2">
                {requestForm.request_type === 'controle' && 'De leverancier beoordeelt uw document en geeft feedback'}
                {requestForm.request_type === 'advies' && 'Er wordt een afspraak ingepland voor toelichting en ondersteuning'}
                {requestForm.request_type === 'template' && 'U ontvangt een template-module om uw documentatie op orde te krijgen'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRequestModal(false)}>Annuleren</Button>
            <Button 
              className="bg-black text-white hover:bg-neutral-800"
              onClick={handleServiceRequest}
              disabled={requestSending}
              data-testid="submit-request-btn"
            >
              {requestSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <>
                <Send className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />Aanvraag versturen
              </>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* =============== STATUS TRANSITIE DIALOG =============== */}
      <Dialog open={showTransitionModal} onOpenChange={setShowTransitionModal}>
        <DialogContent className="sm:max-w-md" data-testid="transition-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Status wijzigen</DialogTitle>
            <DialogDescription className="text-xs">
              {transitionDoc?.name}, {transitionDoc?.status} → {transitionTarget}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 bg-neutral-50 rounded-xl border border-neutral-100">
              <div className="flex items-center gap-3">
                <Badge className={
                  transitionTarget === 'gepubliceerd' ? 'bg-emerald-100 text-emerald-700' :
                  transitionTarget === 'goedgekeurd' ? 'bg-blue-100 text-blue-700' :
                  transitionTarget === 'review' ? 'bg-amber-100 text-amber-700' :
                  'bg-neutral-100 text-neutral-600'
                }>
                  {transitionTarget === 'gepubliceerd' ? 'Publiceren' :
                   transitionTarget === 'goedgekeurd' ? 'Goedkeuren' :
                   transitionTarget === 'review' ? 'Naar review' :
                   transitionTarget === 'concept' ? 'Terug naar concept' : transitionTarget}
                </Badge>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-medium">Opmerking (optioneel)</Label>
              <Textarea
                value={transitionComment}
                onChange={e => setTransitionComment(e.target.value)}
                placeholder="Voeg een opmerking toe bij deze statuswijziging..."
                rows={3}
                className="text-sm"
                data-testid="transition-comment"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowTransitionModal(false)}>Annuleren</Button>
            <Button size="sm" className="bg-black text-white hover:bg-neutral-800" onClick={handleTransition} disabled={transitionSaving} data-testid="confirm-transition-btn">
              {transitionSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Bevestigen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* =============== NIEUW BELEIDSDOCUMENT DIALOG =============== */}
      <Dialog open={showNewPolicyModal} onOpenChange={setShowNewPolicyModal}>
        <DialogContent className="sm:max-w-md" data-testid="new-policy-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Nieuw beleidsdocument</DialogTitle>
            <DialogDescription className="text-xs">Maak een nieuw document aan. Het start automatisch als concept.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-xs font-medium">Naam *</Label>
              <Input value={newPolicyForm.name} onChange={e => setNewPolicyForm(f => ({ ...f, name: e.target.value }))} placeholder="bijv. Privacy Policy" data-testid="new-policy-name" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-medium">Categorie</Label>
              <Select value={newPolicyForm.category} onValueChange={v => setNewPolicyForm(f => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="new-policy-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['Governance', 'Risicobeheer', 'Operationeel', 'Continuïteit', 'Supply Chain', 'Overig'].map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-medium">Beschrijving</Label>
              <Textarea value={newPolicyForm.description} onChange={e => setNewPolicyForm(f => ({ ...f, description: e.target.value }))} placeholder="Korte omschrijving..." rows={2} className="text-sm" data-testid="new-policy-description" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-medium">Eigenaar</Label>
                <Input value={newPolicyForm.owner} onChange={e => setNewPolicyForm(f => ({ ...f, owner: e.target.value }))} placeholder="bijv. CISO" data-testid="new-policy-owner" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium">Reviewer</Label>
                <Input value={newPolicyForm.reviewer} onChange={e => setNewPolicyForm(f => ({ ...f, reviewer: e.target.value }))} placeholder="bijv. CTO" data-testid="new-policy-reviewer" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowNewPolicyModal(false)}>Annuleren</Button>
            <Button size="sm" className="bg-black text-white hover:bg-neutral-800" onClick={handleCreatePolicy} disabled={newPolicySaving} data-testid="save-new-policy-btn">
              {newPolicySaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Aanmaken'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </PageShell>
  );
};

export default StrategiePage;
